<script setup lang="ts">
import messages from '~/messages/messages'
import { languages } from '~/messages/languages'

interface Country {
  code: string
  name: string
  url: string
  locale: string
}

interface Props {
  detectedCountryCode?: string
  currentSiteCountryCode?: string
}

interface Emits {
  (e: 'close'): void
  (e: 'switch', countryCode: string): void
}

const props = withDefaults(defineProps<Props>(), {
  detectedCountryCode: '',
  currentSiteCountryCode: '',
})

const emit = defineEmits<Emits>()

const dialog = ref()
const selectedCountry = ref('')
const currentLocale = useCurrentLocale()

// Dynamically build country list from available languages in messages
const allCountries = computed<Country[]>(() => {
  const countries: Country[] = []

  // Country code to primary locale mapping (which locale to use for each country's site)
  // ONLY includes locales that exist in languages.ts
  const countryToLocale: { [key: string]: string } = {
    AT: 'at',
    BE: 'nl', // Belgium defaults to Dutch
    DE: 'de',
    ES: 'es',
    FI: 'fi',
    FR: 'fra',
    GB: 'en',
    IT: 'it',
    NL: 'nd',
    PL: 'pl',
    PT: 'pt',
    SE: 'sv',
  }

  // Build countries ONLY from locales that exist in languages.ts (same as os-flag)
  for (const [countryCode, locale] of Object.entries(countryToLocale)) {
    // Only include if locale exists in languages array (same check as os-flag)
    if (languages.includes(locale)) {
      const messageData = messages[locale as keyof typeof messages]
      if (messageData && messageData['route.home']) {
        countries.push({
          code: countryCode,
          name: messageData['language.name'] as string, // Use language.name from messages
          url: messageData['route.home'] as string,
          locale: locale,
        })
      }
    }
  }

  return countries
})

// Sort countries with detected country first, then alphabetically
const sortedCountries = computed(() => {
  const detectedCode = props.detectedCountryCode.toUpperCase()
  const currentSiteCode = props.currentSiteCountryCode.toUpperCase()

  // Add detection flags to countries
  const countriesWithFlags = allCountries.value.map((c) => ({
    ...c,
    isDetected: c.code === detectedCode,
    isCurrentSite: c.code === currentSiteCode,
  }))

  // Sort: current site first, then detected country, then alphabetically
  return countriesWithFlags.sort((a, b) => {
    // Current site always first
    if (a.isCurrentSite && !b.isCurrentSite) return -1
    if (!a.isCurrentSite && b.isCurrentSite) return 1

    // Then detected country
    if (a.isDetected && !b.isDetected) return -1
    if (!a.isDetected && b.isDetected) return 1

    // Then alphabetically by name
    return a.name.localeCompare(b.name)
  })
})

function open() {
  // Pre-select the current site's country
  selectedCountry.value =
    props.currentSiteCountryCode.toUpperCase() ||
    props.detectedCountryCode.toUpperCase()
  dialog.value?.open()
}

function close() {
  dialog.value?.close()
}

function handleConfirm() {
  if (selectedCountry.value) {
    const country = allCountries.value.find(
      (c) => c.code === selectedCountry.value,
    )
    if (country) {
      // Mark as handled before redirecting
      emit('close')

      // If different country selected, redirect to that country's URL
      if (
        selectedCountry.value !== props.currentSiteCountryCode.toUpperCase()
      ) {
        window.location.href = country.url
      } else {
        dialog.value?.close()
      }
    }
  }
}

function handleCancel() {
  dialog.value?.close()
}

defineExpose({
  open,
  close,
})
</script>

<template>
  <os-dialog
    ref="dialog"
    :title="$t('country_selector.title')"
    size="default"
    @close="() => emit('close')"
    class="country-selector-dialog"
  >
    <os-column cross-axis-alignment="stretch" class="country-selector-content">
      <p class="country-selector-content__message">
        {{ $t('country_selector.message') }}
      </p>

      <ion-radio-group
        v-model="selectedCountry"
        class="country-selector-content__list"
      >
        <div
          v-for="country in sortedCountries"
          :key="country.code"
          class="country-option"
          :class="{
            'country-option--selected': selectedCountry === country.code,
            'country-option--current': country.isCurrentSite,
            'country-option--detected':
              country.isDetected && !country.isCurrentSite,
          }"
          @click="selectedCountry = country.code"
        >
          <ion-radio :value="country.code" class="country-option__radio" />
          <div class="country-option__flag">
            <os-flag :language="country.locale" />
          </div>
          <div class="country-option__info">
            <span class="country-option__name">{{ country.name }}</span>
            <!--<span
              v-if="country.isCurrentSite"
              class="country-option__badge country-option__badge--current"
            >
             {{ $t('country_selector.current_site') }}
            </span>
            <span
              v-else-if="country.isDetected"
              class="country-option__badge country-option__badge--suggested"
            >
              {{ $t('country_selector.suggested') }}
            </span>-->
          </div>
        </div>
      </ion-radio-group>

      <os-column
        cross-axis-alignment="stretch"
        class="country-selector-actions"
      >
        <ion-button
          expand="block"
          @click="handleConfirm"
          :disabled="!selectedCountry"
          class="country-selector-actions__confirm"
        >
          {{ $t('country_selector.confirm') }}
          <ion-icon :icon="ioniconsChevronForward" slot="end" />
        </ion-button>
        <!-- <ion-button
          expand="block"
          fill="outline"
          @click="handleCancel"
          class="country-selector-actions__cancel"
        >
          {{ $t('country_selector.cancel') }}
        </ion-button> -->
      </os-column>
    </os-column>
  </os-dialog>
</template>

<style scoped>
/* Center the dialog title */
.country-selector-dialog :deep(h1) {
  text-align: center !important;
}

.country-selector-dialog :deep(h2) {
  text-align: center !important;
}

.country-selector-dialog :deep(.os-dialog__content__title) {
  text-align: center !important;
}

.country-selector-dialog :deep(.os-dialog__toolbar__title) {
  text-align: center !important;
}

.country-selector-content {
  gap: 1.5rem;
  padding: 0.5rem 0;
}

.country-selector-content__message {
  text-align: center;
  line-height: 1.6;
  font-size: 0.95rem;
  color: var(--header);
  margin: 0;
}

.country-selector-content__list {
  max-height: 50vh;
  overflow-y: auto;
  border: 1px solid var(--background--primary);
  border-radius: 0.5rem;
  padding: 0.5rem;
}

.country-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: 2px solid transparent;
}

.country-option:hover {
  background-color: var(--background--primary);
}

.country-option--selected {
  border-color: var(--accent);
}

.country-option--current {
  background-color: var(--accent--transparent);
}

.country-option--detected {
  /* different color if needed */
  /* background: linear-gradient(90deg, var(--accent--transparent) 0%, transparent 100%);*/
}

.country-option__radio {
  margin: 0;
  flex-shrink: 0;
}

.country-option__flag {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.country-option__flag :deep(.os-flag) {
  width: 32px !important;
  height: 32px !important;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.country-option__info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.country-option__name {
  font-weight: 500;
  color: var(--header);
  font-size: 0.95rem;
}

.country-option__badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.country-option__badge--current {
  background-color: var(--green);
}

.country-option__badge--suggested {
  background-color: var(--accent);
}

.country-selector-actions {
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.country-selector-actions__confirm {
  font-weight: 600;
  min-height: 3rem;
}

.country-selector-actions__cancel {
  --color: var(--header);
  --border-color: #d0d0d0;
  --border-width: 2px;
  font-weight: 500;
  opacity: 0.9;
}

/* Scrollbar styling */
.country-selector-content__list::-webkit-scrollbar {
  width: 8px;
}

.country-selector-content__list::-webkit-scrollbar-track {
  background: var(--background--primary);
  border-radius: 4px;
}

.country-selector-content__list::-webkit-scrollbar-thumb {
  background: var(--accent);
  border-radius: 4px;
}

.country-selector-content__list::-webkit-scrollbar-thumb:hover {
  background: var(--header);
}

@media (max-width: 639px) {
  .country-selector-content__list {
    max-height: 60vh;
  }

  .country-option {
    padding: 0.65rem 0.75rem;
  }

  .country-option__flag {
    width: 28px;
    height: 28px;
  }

  .country-option__name {
    font-size: 0.9rem;
  }

  .country-option__badge {
    font-size: 0.65rem;
    padding: 0.2rem 0.4rem;
  }
}
</style>
